// player/Timeline.tsx — the scrubber (1.27.50, owner concept video_watch_page_v2): the
// played and buffered ranges, chapter segments with small gaps, and — on hover or while
// dragging — a preview frame with the time and the chapter title.
//
// The preview frame comes from a second, hidden, muted copy of the light rendition seeked to
// the hovered time and drawn onto a canvas. Drawing a cross-origin video onto a canvas is
// allowed without CORS (the canvas is only ever displayed, never read), so this needs
// nothing from the server.
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { chapterAt, formatClock, type Chapter } from './chapters';

const INK = '#fff';

const Track = styled.div<{ $large: boolean }>`
  position: relative;
  height: ${({ $large }) => ($large ? 22 : 16)}px;
  display: flex;
  align-items: center;
  cursor: pointer;
  touch-action: none;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const Segments = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 3px;
`;

const Segment = styled.div<{ $grow: number; $hot: boolean }>`
  position: relative;
  flex: ${({ $grow }) => $grow} 1 0;
  height: ${({ $hot }) => ($hot ? 6 : 4)}px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.24);
  overflow: hidden;
  transition: height 120ms ease;
`;

const Fill = styled.div<{ $kind: 'played' | 'buffered' }>`
  position: absolute;
  inset: 0 auto 0 0;
  background: ${({ $kind, theme }) =>
    $kind === 'played' ? theme.color.accentCyan : 'rgba(255, 255, 255, 0.28)'};
`;

const Thumb = styled.div<{ $large: boolean }>`
  position: absolute;
  top: 50%;
  width: ${({ $large }) => ($large ? 16 : 13)}px;
  height: ${({ $large }) => ($large ? 16 : 13)}px;
  margin-left: ${({ $large }) => ($large ? -8 : -6.5)}px;
  border-radius: 50%;
  background: ${INK};
  box-shadow: 0 0 0 3px ${({ theme }) => theme.color.accentCyan}2e;
  transform: translateY(-50%);
  pointer-events: none;
`;

const Preview = styled.div`
  position: absolute;
  bottom: calc(100% + 10px);
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  pointer-events: none;
  z-index: 3;
`;

const PreviewFrame = styled.canvas`
  width: 160px;
  height: 90px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: #000;
`;

const PreviewText = styled.span`
  display: flex;
  column-gap: 0.9em;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(10, 8, 18, 0.88);
  color: ${INK};
  font: 600 12px ${({ theme }) => theme.font.body};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  max-width: 220px;
  overflow: hidden;
  & > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export interface TimelineProps {
  current: number;
  duration: number;
  bufferedEnd: number;
  chapters: Chapter[];
  /** The light rendition — the source for hover previews. */
  previewSrc?: string;
  large?: boolean;
  onSeek: (t: number) => void;
  /** Dragging starts/stops (the player keeps its controls up meanwhile). */
  onScrubbing?: (active: boolean) => void;
}

export function Timeline({
  current,
  duration,
  bufferedEnd,
  chapters,
  previewSrc,
  large = false,
  onSeek,
  onScrubbing,
}: TimelineProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [hoverAt, setHoverAt] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);
  const canPreview = Boolean(previewSrc) && duration > 0;

  const timeFromX = useCallback(
    (clientX: number) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || rect.width <= 0 || duration <= 0) return 0;
      const f = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      return f * duration;
    },
    [duration],
  );

  // Segments: the chapters, or one segment for the whole video.
  const parts = chapters.length > 0 ? chapters : [{ start: 0, end: duration || 1, title: '' }];
  const shown = hoverAt ?? (dragging ? current : null);
  const hotChapter = shown !== null ? chapterAt(chapters, shown) : undefined;

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (duration <= 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
    onScrubbing?.(true);
    onSeek(timeFromX(e.clientX));
  }
  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const t = timeFromX(e.clientX);
    if (e.pointerType === 'mouse' || dragging) setHoverAt(t);
    if (dragging) onSeek(t);
  }
  function endDrag() {
    if (!dragging) return;
    setDragging(false);
    onScrubbing?.(false);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const step = e.shiftKey ? 10 : 5;
    let next: number | null = null;
    if (e.key === 'ArrowLeft') next = current - step;
    else if (e.key === 'ArrowRight') next = current + step;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = duration;
    else if (e.key === 'PageUp' || e.key === 'PageDown') {
      // Chapter hops.
      const i = chapters.findIndex((c) => current >= c.start && current < c.end);
      const target = chapters[e.key === 'PageUp' ? i + 1 : Math.max(0, i - 1)];
      if (target) next = target.start;
    }
    if (next === null) return;
    e.preventDefault();
    e.stopPropagation();
    onSeek(Math.min(duration, Math.max(0, next)));
  }

  const valueText = `${formatClock(current)} of ${formatClock(duration)}${
    chapters.length ? `, ${chapterAt(chapters, current)?.title ?? ''}` : ''
  }`;

  return (
    <Track
      ref={trackRef}
      $large={large}
      role="slider"
      tabIndex={0}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.round(duration)}
      aria-valuenow={Math.round(current)}
      aria-valuetext={valueText}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={() => !dragging && setHoverAt(null)}
      onKeyDown={onKeyDown}
      data-testid="player-timeline"
    >
      <Segments>
        {parts.map((c) => {
          const len = Math.max(0.001, c.end - c.start);
          const played = Math.min(1, Math.max(0, (current - c.start) / len));
          const buffered = Math.min(1, Math.max(0, (bufferedEnd - c.start) / len));
          return (
            <Segment
              key={c.start}
              $grow={len}
              $hot={hotChapter === c || (!chapters.length && shown !== null)}
              data-testid="timeline-segment"
            >
              <Fill $kind="buffered" style={{ width: `${buffered * 100}%` }} />
              <Fill $kind="played" style={{ width: `${played * 100}%` }} />
            </Segment>
          );
        })}
      </Segments>
      {duration > 0 && <Thumb $large={large} style={{ left: `${(current / duration) * 100}%` }} />}
      {shown !== null && duration > 0 && (
        <Preview style={{ left: `clamp(84px, ${(shown / duration) * 100}%, calc(100% - 84px))` }}>
          {canPreview && <PreviewCanvas src={previewSrc!} at={shown} />}
          <PreviewText data-testid="timeline-preview">
            <span>{formatClock(shown)}</span>
            {hotChapter?.title && <span>{hotChapter.title}</span>}
          </PreviewText>
        </Preview>
      )}
    </Track>
  );
}

/** The hidden seeker behind the hover frame. Mounted only while a preview shows. */
function PreviewCanvas({ src, at }: { src: string; at: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pending = useRef<number | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    const v = document.createElement('video');
    v.muted = true;
    v.preload = 'auto';
    v.playsInline = true;
    v.src = src;
    const paint = () => {
      busy.current = false;
      const c = canvasRef.current;
      const ctx = c?.getContext('2d');
      if (c && ctx) {
        try {
          ctx.drawImage(v, 0, 0, c.width, c.height);
        } catch {
          // decoder not ready — the next seek draws
        }
      }
      if (pending.current !== null) {
        const next = pending.current;
        pending.current = null;
        busy.current = true;
        v.currentTime = next;
      }
    };
    // "seeked" can fire a moment before the new frame is paintable — paint on the next frame,
    // and again once frame data first arrives.
    const draw = () => requestAnimationFrame(paint);
    v.addEventListener('seeked', draw);
    v.addEventListener('loadeddata', draw);
    videoRef.current = v;
    return () => {
      v.removeEventListener('seeked', draw);
      v.removeEventListener('loadeddata', draw);
      v.removeAttribute('src');
      v.load();
      videoRef.current = null;
    };
  }, [src]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // One seek at a time; the latest hover wins.
    if (busy.current) pending.current = at;
    else {
      busy.current = true;
      try {
        v.currentTime = at;
      } catch {
        busy.current = false;
      }
    }
  }, [at]);

  return <PreviewFrame ref={canvasRef} width={160} height={90} aria-hidden="true" />;
}
